import { QueryClient, MutationCache } from '@tanstack/react-query';
import { toast } from 'sonner';
import i18n from '@/i18n';
import { resolveErrorMessage, type ApiErrorLike, type ErrorVia } from './errors/resolveErrorMessage';
// Store del modal de upsell (Fase 2 planes). La dependencia va en un solo
// sentido — `planLimitStore` NO importa de este módulo — así que no hay ciclo.
import { openPlanLimit } from '@/stores/planLimitStore';

interface ToastApiErrorOptions {
  /** Optional fallback. Kept for backwards compatibility; prefer the i18n catalog. */
  fallback?: string;
  /** When provided, the toast renders an action button. */
  onRetry?: () => void;
  /** Override the default duration (ms). */
  duration?: number;
  /**
   * F5 · la operación que produjo el error (emisión, anulación, reembolso, rectificativa,
   * registro). El mismo `code` dice cosas distintas en cada una; ver `resolveErrorMessage`.
   */
  via?: ErrorVia;
}

/**
 * Render a user-friendly toast for any API error.
 *
 * - Reads only the machine-readable `code` (and `status` as fallback) — the
 *   backend's raw `message` is ignored intentionally so technical strings
 *   (`fetch failed`, Prisma error text, AWS SDK messages) NEVER reach the user.
 * - Picks the toast type based on severity: errors are red, validation/maintenance
 *   are warnings, and feature-availability messages render as info.
 * - Appends a short `Ref:` token from the backend correlation id so support can
 *   match the report to a CloudWatch log without exposing internals.
 *
 * Backwards-compatible signatures:
 *   toastApiError(error)
 *   toastApiError(error, 'Error al guardar')   // legacy fallback string, ignored when i18n resolves
 *   toastApiError(error, { onRetry })
 */
export function toastApiError(
  error: unknown,
  fallbackOrOptions?: string | ToastApiErrorOptions,
): void {
  const opts: ToastApiErrorOptions =
    typeof fallbackOrOptions === 'string'
      ? { fallback: fallbackOrOptions }
      : (fallbackOrOptions ?? {});

  const apiErr = (error && typeof error === 'object' ? error : {}) as ApiErrorLike;

  // Fase 2 planes — cuota superada: abre el modal de upsell en vez de un toast.
  // Cubre todos los puntos de mutación que ya pasan por `toastApiError` (factura,
  // OCR, banco, invitar) sin editarlos uno a uno.
  if (apiErr.code === 'PLAN_QUOTA_EXCEEDED') {
    openPlanLimit(apiErr.details as Record<string, unknown> | undefined);
    return;
  }

  const { title, description, severity, isGeneric } = resolveErrorMessage(apiErr, undefined, { via: opts.via });

  // La referencia técnica se pega a la descripción. Con `ref` (requestId del
  // backend) siempre; sin ella, SOLO cuando el copy es genérico —«Esta acción no
  // se puede completar en este momento», «Revisa los campos marcados»—, que es
  // justo cuando el toast no dice nada y quien da soporte se queda sin nada que
  // buscar: en el smoke de agosto, «no se pudo crear la subcuenta» no permitía
  // distinguir plan contable sin sembrar, permiso insuficiente y 500.
  //
  // Es un identificador para soporte, no una explicación: `status` y `code` son
  // etiquetas de máquina cortas, nunca el `message` del backend (que es texto
  // libre y puede filtrar interioridades — por eso `toast.error(error.message)`
  // está prohibido).
  const ref = (apiErr as { ref?: string }).ref;
  const referencia = ref
    ? `Ref: ${ref.slice(0, 8)}`
    : isGeneric
      ? [apiErr.status, apiErr.code].filter(Boolean).join(' · ')
      : '';
  const decoratedDesc = referencia
    ? `${description ?? ''}${description ? ' · ' : ''}${referencia}`
    : description;

  const toastFn = severity === 'info'
    ? toast.info
    : severity === 'warning'
      ? toast.warning
      : toast.error;

  toastFn(title, {
    description: decoratedDesc,
    duration: opts.duration ?? (apiErr.status === 429 ? 8000 : 5000),
    action: opts.onRetry
      ? { label: i18n.t('common.retry') as unknown as string, onClick: opts.onRetry }
      : undefined,
  });
}

export const queryClientInstance = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    },
  },
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      // Only show the global toast if the mutation didn't define its own onError.
      if (!mutation.options.onError) {
        toastApiError(error);
      }
    },
  }),
});
